import type { FilterJourneyIndex } from '@/src/types/srpJourneyIndex'
import { useTranslations } from 'next-intl'
import { useCallback, useMemo } from 'react'
import useClevertapForSrp from '../../analytics/clevertap/srp'
import useActiveFilterDataIndex from '../useActiveFilterDataIndex'
import useUpdateSearchResultCache from '../updateSearchResultCache'
import useSearchResults from '../useSearchResults'

const useLayoverDurationFilterManager = (filterJourneyIndex: FilterJourneyIndex = 0) => {
    const t = useTranslations('common')
    const { triggerSrpEvent } = useClevertapForSrp()
    const updateCache = useUpdateSearchResultCache()
    const { filterData, isMulticity } = useSearchResults()
    const { filterDataIndex } = useActiveFilterDataIndex(filterJourneyIndex)
    const targetLayoverDuration = filterData?.layoverDuration?.[filterDataIndex]
    const { min: defaultMin, max: defaultMax, current } = targetLayoverDuration || {}
    const { min: currentMin, max: currentMax } = current || {}

    const title = t('layoverDuration')

    const changeLayoverDurationRange = useCallback(
        (min: number, max: number) => {
            triggerSrpEvent('Air_UI_Action', {
                action_name: 'filter',
                action_type: 'layover_duration',
                action_value: `${min}-${max}`
            })

            updateCache(currentData => {
                if (currentData?.filterData?.layoverDuration?.[filterDataIndex]) {
                    currentData.filterData.layoverDuration[filterDataIndex].current = { min, max }
                }
                return currentData as NonNullable<typeof currentData>
            })
        },
        [filterDataIndex, triggerSrpEvent, updateCache]
    )

    const handleReset = useCallback(() => {
        const numDefaultMin = Number(defaultMin)
        const numDefaultMax = Number(defaultMax)
        if (!isNaN(numDefaultMin) && !isNaN(numDefaultMax)) {
            triggerSrpEvent('Air_UI_Action', {
                action_name: 'filter',
                action_type: 'reset',
                action_value: 'layover_duration'
            })

            updateCache(currentData => {
                if (currentData?.filterData?.layoverDuration?.[filterDataIndex]) {
                    currentData.filterData.layoverDuration[filterDataIndex].current = {
                        min: numDefaultMin,
                        max: numDefaultMax
                    }
                }
                return currentData as NonNullable<typeof currentData>
            })
        }
    }, [defaultMax, defaultMin, filterDataIndex, triggerSrpEvent, updateCache])

    const isLayoverDurationFilterApplied = useMemo(() => {
        return currentMin !== defaultMin || currentMax !== defaultMax
    }, [currentMax, currentMin, defaultMax, defaultMin])

    const shouldShowResetForAllJourneys = useMemo(() => {
        if (!isMulticity) return false
        return !!(filterData?.layoverDuration?.some(ld => ld.current?.min !== ld.min || ld.current?.max !== ld.max))
    }, [isMulticity, filterData])

    const handleResetAllJourneys = useCallback(() => {
        triggerSrpEvent('Air_UI_Action', {
            action_name: 'filter',
            action_type: 'reset',
            action_value: 'layover_duration'
        })

        updateCache(currentData => {
            currentData?.filterData?.layoverDuration?.forEach(ld => {
                ld.current = { min: ld.min, max: ld.max }
            })
            return currentData as NonNullable<typeof currentData>
        })
    }, [triggerSrpEvent, updateCache])

    return {
        title,
        min: defaultMin,
        max: defaultMax,
        currentMin,
        currentMax,
        changeLayoverDurationRange,
        handleReset,
        handleResetAllJourneys,
        isLayoverDurationFilterApplied,
        shouldShowResetForAllJourneys
    }
}

export default useLayoverDurationFilterManager
